/**
 * @zendir/ui - ChatPanel Component
 *
 * Status-aware chat interface for operator dashboards with AI/command assistance.
 * Supports structured message blocks (alerts, telemetry, status cards, data tables)
 * alongside plain text, markdown, and code.
 *
 * Astro UX Compliance:
 * - 6-level status system with dual-coded shapes (color + shape)
 * - Glass-morphism for message bubbles
 * - Monospace for code/command content
 * - Focus management and keyboard shortcuts
 *
 * AI Integration:
 * - Messages can include structured `blocks` that the AI emits
 * - Four integration strategies: Tool/Function Calling, YAML, JSON, and MCP
 * - Tool/Function calling schemas for OpenAI, Anthropic, Gemini, and MCP servers
 * - `parseChatResponse()` — universal parser for JSON, YAML, and plain text
 * - `parseMcpToolResult()` — bridge MCP tool results to ChatPanel blocks
 * - `CHAT_RESPONSE_TOOL_SCHEMA` — drop-in function schema for LLM API calls
 * - `CHAT_RESPONSE_MCP_TOOL` — MCP server tool definition for structured responses
 * - `CHAT_RESPONSE_YAML_PROMPT` — system prompt snippet for YAML mode
 *
 * @example
 * ```tsx
 * <ChatPanel
 *   messages={messages}
 *   onSend={(text) => handleSend(text)}
 *   loading={isStreaming}
 *   placeholder="Ask about telemetry..."
 *   title="AI Assistant"
 * />
 * ```
 */

import React, { memo, useState, useCallback, useEffect, useRef } from "react";
import { useTheme } from "../../theme";
import { safeAccentText } from "../../utils";
import type { StatusLevel } from "../../utils";

// ─── Types ───────────────────────────────────────────────────────────────────

/** Status alert block — renders as a colored banner with icon shape */
export interface ChatBlockAlert {
  type: "alert";
  status: StatusLevel;
  title: string;
  detail?: string;
}

/** Telemetry readout block — key/value pairs with optional status per row */
export interface ChatBlockTelemetry {
  type: "telemetry";
  title?: string;
  items: Array<{
    label: string;
    value: string | number;
    unit?: string;
    status?: StatusLevel;
  }>;
}

/** Progress block — inline progress bar */
export interface ChatBlockProgress {
  type: "progress";
  label: string;
  value: number;
  max?: number;
  status?: StatusLevel;
}

/** Data table block */
export interface ChatBlockTable {
  type: "table";
  title?: string;
  columns: string[];
  rows: (string | number)[][];
}

/** Action buttons block */
export interface ChatBlockActions {
  type: "actions";
  buttons: Array<{
    label: string;
    variant?: "primary" | "secondary" | "critical";
    onClick: () => void;
  }>;
}

/** Choice/option selector — AI presents options, user picks one (or multiple) */
export interface ChatBlockChoice {
  type: "choice";
  /** Unique ID for this choice block (used in onBlockEvent callback) */
  id: string;
  title?: string;
  /** Allow multiple selections */
  multiple?: boolean;
  options: Array<{
    id: string;
    label: string;
    description?: string;
    status?: StatusLevel;
    disabled?: boolean;
  }>;
  /** Pre-selected option IDs */
  selected?: string[];
  /** Has the user already submitted their choice? */
  submitted?: boolean;
}

/** Confirmation gate — requires explicit operator acknowledgment before proceeding */
export interface ChatBlockConfirm {
  type: "confirm";
  /** Unique ID for this confirm block (used in onBlockEvent callback) */
  id: string;
  status?: StatusLevel;
  title: string;
  detail?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Has the operator already responded? */
  resolved?: "confirmed" | "cancelled";
}

/** Command block — displays a command with copy-to-clipboard and optional execute */
export interface ChatBlockCommand {
  type: "command";
  /** Unique ID for this command block (used in onBlockEvent callback) */
  id?: string;
  language?: string;
  command: string;
  label?: string;
  /** Show an execute button (fires onBlockEvent with action:'execute') */
  executable?: boolean;
}

/** Key-value metadata block — compact pairs for context/metadata */
export interface ChatBlockKV {
  type: "kv";
  title?: string;
  pairs: Array<{ key: string; value: string | number; mono?: boolean }>;
}

export type ChatBlock =
  | ChatBlockAlert
  | ChatBlockTelemetry
  | ChatBlockProgress
  | ChatBlockTable
  | ChatBlockActions
  | ChatBlockChoice
  | ChatBlockConfirm
  | ChatBlockCommand
  | ChatBlockKV;

/** Event emitted by interactive blocks (choice, confirm, command) */
export interface ChatBlockEvent {
  /** The block ID */
  blockId: string;
  /** The message ID containing this block */
  messageId: string;
  /** Action type */
  action: "select" | "confirm" | "cancel" | "execute" | "copy";
  /** Payload (selected option IDs for choice, command string for execute/copy) */
  value?: string | string[];
}

export interface ChatMessage {
  /** Unique message ID */
  id: string;
  /** Message content (supports markdown-like formatting) */
  content: string;
  /** Sender role */
  role: "user" | "assistant" | "system";
  /** Timestamp (ms) */
  timestamp?: number;
  /** Message delivery status */
  status?: "sending" | "sent" | "error";
  /** Astro UX severity — colors the message accent (system messages default to this) */
  severity?: StatusLevel;
  /** Structured blocks rendered below the text content */
  blocks?: ChatBlock[];
  /** Legacy: action buttons (prefer blocks with type:'actions') */
  actions?: Array<{ label: string; onClick: () => void }>;
}

export interface ChatPanelProps {
  /** Array of messages */
  messages: ChatMessage[];
  /** Send handler */
  onSend: (text: string) => void;
  /** Is assistant currently generating */
  loading?: boolean;
  /** Input placeholder */
  placeholder?: string;
  /** Panel title */
  title?: string;
  /** Subtitle (e.g. model name) */
  subtitle?: string;
  /** Disable input */
  disabled?: boolean;
  /** Show timestamps */
  showTimestamps?: boolean;
  /** Height of the panel (default: 100%) */
  height?: string | number;
  /** Clear handler (shows clear button when provided) */
  onClear?: () => void;
  /** Handler for interactive block events (choice selection, confirm/cancel, command execute) */
  onBlockEvent?: (event: ChatBlockEvent) => void;
  /**
   * Render a message's text yourself instead of using the built-in renderer.
   *
   * The built-in one covers `**bold**`, `` `code` `` and fenced code blocks.
   * Anything richer — lists, tables, links — needs a real markdown renderer,
   * which is a dependency this package does not carry. Supply one here to hand
   * that decision (and its bundle cost) to the host.
   *
   * Blocks are unaffected; they render as normal beneath the returned content.
   */
  renderMessageContent?: (content: string, message: ChatMessage) => React.ReactNode;
  /** Custom style */
  style?: React.CSSProperties;
}

// ─── Response format & parser ────────────────────────────────────────────────

/**
 * Supported response formats from the AI backend.
 * - 'json':  Standard JSON (default). Most compatible.
 * - 'yaml':  YAML compact format. ~30-40% fewer tokens than JSON for identical data.
 * - 'auto':  Auto-detect (tries JSON first, then YAML, then plain text).
 */
export type ChatResponseFormat = "json" | "yaml" | "auto";

/** Shape of a parsed AI response (content + optional blocks + severity). */
export interface ChatResponsePayload {
  content: string;
  severity?: StatusLevel;
  blocks?: ChatBlock[];
}

/** Options for creating a customised response parser. */
export interface ChatResponseParserOptions {
  /**
   * Custom YAML parser function. Receives raw YAML text, returns a parsed object.
   * If not provided, uses the built-in lightweight parser.
   *
   * For production, plug in `js-yaml` or `yaml`:
   * ```ts
   * import yaml from 'js-yaml';
   * const parse = createChatResponseParser({ yamlParser: (s) => yaml.load(s) });
   * ```
   */
  yamlParser?: (text: string) => unknown;
  /** Default format when not specified per-call. Defaults to `'auto'`. */
  defaultFormat?: ChatResponseFormat;
  /** Custom message ID generator. Defaults to `crypto.randomUUID()`. */
  idGenerator?: () => string;
}

// ─── Built-in YAML parser (recursive descent) ───────────────────────────────

/**
 * Lightweight recursive-descent YAML parser for AI response structures.
 *
 * Handles: mappings, sequences, nested structures, scalars (string, number,
 * boolean, null), quoted strings, inline JSON arrays/objects, comments.
 *
 * Does NOT handle: multi-line strings, anchors/aliases, tags, flow collections
 * across lines, merge keys, or other advanced YAML features.
 *
 * For production with complex YAML, plug in `js-yaml` or `yaml` via
 * `createChatResponseParser({ yamlParser })`.
 */
function parseYamlLite(text: string): unknown {
  type Line = { indent: number; text: string };
  const lines: Line[] = [];
  for (const raw of text.split("\n")) {
    const content = raw.trimEnd();
    if (content.trim() === "" || content.trim().startsWith("#")) continue;
    lines.push({ indent: content.search(/\S/), text: content.trim() });
  }

  let pos = 0;
  const peek = (): Line | null => (pos < lines.length ? lines[pos] : null);
  const advance = (): Line => lines[pos++];

  function scalar(s: string): unknown {
    const v = s.trim();
    if (v === "" || v === "null" || v === "~") return null;
    if (v === "true") return true;
    if (v === "false") return false;
    if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(v)) return Number(v);
    if (
      (v.startsWith('"') && v.endsWith('"')) ||
      (v.startsWith("'") && v.endsWith("'"))
    )
      return v.slice(1, -1);
    if (
      (v.startsWith("[") && v.endsWith("]")) ||
      (v.startsWith("{") && v.endsWith("}"))
    ) {
      try {
        return JSON.parse(v);
      } catch {
        /* fall through */
      }
    }
    return v;
  }

  function parseValue(minIndent: number): unknown {
    const line = peek();
    if (!line || line.indent < minIndent) return undefined;
    const actualIndent = line.indent;
    if (line.text.startsWith("- ")) return parseSequence(actualIndent);
    return parseMapping(actualIndent);
  }

  function parseSequence(level: number): unknown[] {
    const arr: unknown[] = [];
    while (
      peek() &&
      peek()!.indent === level &&
      peek()!.text.startsWith("- ")
    ) {
      const line = advance();
      const after = line.text.slice(2).trim();

      if (!after) {
        // Bare `- ` → nested value on next lines
        const val = parseValue(level + 1);
        arr.push(val ?? null);
        continue;
      }

      const colonIdx = after.indexOf(":");
      if (colonIdx > 0 && !/^['"]/.test(after)) {
        // Inline mapping start: `- key: value`
        const obj: Record<string, unknown> = {};
        const k = after.slice(0, colonIdx).trim();
        const v = after.slice(colonIdx + 1).trim();
        obj[k] = v ? scalar(v) : (parseValue(level + 3) ?? null);

        // Read sibling keys at `level + 2` (aligned with content after `- `)
        const siblingIndent = level + 2;
        while (peek() && peek()!.indent >= siblingIndent) {
          if (
            peek()!.indent === siblingIndent &&
            !peek()!.text.startsWith("- ")
          ) {
            const next = advance();
            const ci = next.text.indexOf(":");
            if (ci > 0) {
              const nk = next.text.slice(0, ci).trim();
              const nv = next.text.slice(ci + 1).trim();
              obj[nk] = nv
                ? scalar(nv)
                : (parseValue(siblingIndent + 1) ?? null);
            }
          } else if (peek()!.indent > siblingIndent) {
            // Deeper content should have been consumed by parseValue above;
            // if not, skip it to avoid infinite loops
            advance();
          } else {
            break;
          }
        }
        arr.push(obj);
      } else {
        // Simple scalar: `- value`
        arr.push(scalar(after));
      }
    }
    return arr;
  }

  function parseMapping(level: number): Record<string, unknown> {
    const obj: Record<string, unknown> = {};
    while (
      peek() &&
      peek()!.indent === level &&
      !peek()!.text.startsWith("- ")
    ) {
      const line = advance();
      const colonIdx = line.text.indexOf(":");
      if (colonIdx <= 0) continue;
      const k = line.text.slice(0, colonIdx).trim();
      const v = line.text.slice(colonIdx + 1).trim();
      obj[k] = v ? scalar(v) : (parseValue(level + 1) ?? null);
    }
    return obj;
  }

  try {
    return parseValue(0) ?? {};
  } catch {
    return {};
  }
}

// ─── Sanitize parsed blocks ─────────────────────────────────────────────────

/** Ensure parsed blocks have the correct array shapes so renderers never crash. */
function sanitizeBlocks(blocks: unknown): ChatBlock[] | undefined {
  if (!Array.isArray(blocks)) return undefined;
  return blocks
    .filter(
      (b): b is Record<string, unknown> =>
        b != null &&
        typeof b === "object" &&
        typeof (b as Record<string, unknown>).type === "string",
    )
    .map((b) => {
      const block = { ...b } as Record<string, unknown>;
      // Ensure required arrays exist for each block type
      switch (block.type) {
        case "telemetry":
          if (!Array.isArray(block.items)) block.items = [];
          break;
        case "table":
          if (!Array.isArray(block.columns)) block.columns = [];
          if (!Array.isArray(block.rows)) block.rows = [];
          break;
        case "actions":
          if (!Array.isArray(block.buttons)) block.buttons = [];
          break;
        case "choice":
          if (!Array.isArray(block.options)) block.options = [];
          if (typeof block.id !== "string") block.id = `choice-${Date.now()}`;
          break;
        case "confirm":
          if (typeof block.id !== "string") block.id = `confirm-${Date.now()}`;
          if (typeof block.title !== "string") block.title = "";
          break;
        case "command":
          if (typeof block.command !== "string") block.command = "";
          break;
        case "kv":
          if (!Array.isArray(block.pairs)) block.pairs = [];
          break;
        case "alert":
          if (typeof block.title !== "string") block.title = "";
          if (typeof block.status !== "string") block.status = "normal";
          break;
        case "progress":
          if (typeof block.label !== "string") block.label = "";
          if (typeof block.value !== "number") block.value = 0;
          break;
      }
      return block as unknown as ChatBlock;
    });
}

// ─── Generate message ID ────────────────────────────────────────────────────

function generateId(custom?: () => string): string {
  if (custom) return custom();
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
    return crypto.randomUUID();
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Core parse logic ───────────────────────────────────────────────────────

function parseRaw(
  raw: string,
  format: ChatResponseFormat,
  yamlParser?: (text: string) => unknown,
): ChatResponsePayload | null {
  const tryJSON = (s: string): ChatResponsePayload | null => {
    try {
      const parsed = JSON.parse(s);
      if (
        parsed &&
        typeof parsed === "object" &&
        typeof parsed.content === "string"
      )
        return parsed;
      return null;
    } catch {
      return null;
    }
  };

  const tryYAML = (s: string): ChatResponsePayload | null => {
    try {
      const parser = yamlParser ?? parseYamlLite;
      const parsed = parser(s);
      if (
        parsed &&
        typeof parsed === "object" &&
        typeof (parsed as Record<string, unknown>).content === "string"
      )
        return parsed as unknown as ChatResponsePayload;
      return null;
    } catch {
      return null;
    }
  };

  if (format === "json") return tryJSON(raw);
  if (format === "yaml") return tryYAML(raw);
  // auto: try JSON first, then YAML
  return tryJSON(raw) ?? tryYAML(raw);
}

/**
 * Parse an AI response string into a `ChatMessage`.
 *
 * Supports three strategies (controlled by `format`):
 * - **json**: Parse as JSON. Falls back to plain text on failure.
 * - **yaml**: Parse as YAML (built-in lightweight parser). ~30-40% fewer tokens.
 * - **auto** (default): Try JSON → YAML → plain text.
 *
 * All parsed blocks are sanitized to ensure required arrays exist, preventing
 * runtime crashes from malformed AI output.
 *
 * ## Why YAML?
 * For the same structured response, YAML uses ~30-40% fewer tokens:
 * ```
 * JSON (~180 tokens): {"content":"Battery low.","blocks":[{"type":"alert",...}]}
 * YAML (~120 tokens): content: Battery low.\nblocks:\n  - type: alert\n    ...
 * ```
 *
 * ## Tool/Function Calling (recommended for production)
 * Pass `CHAT_RESPONSE_TOOL_SCHEMA` in your API call. The model is constrained
 * at the decoding level — zero prompt tokens for the schema.
 *
 * ## Custom YAML parser
 * Use `createChatResponseParser({ yamlParser })` to plug in `js-yaml` or `yaml`.
 *
 * @example
 * ```ts
 * const msg = parseChatResponse(aiOutput);              // auto-detect
 * const msg = parseChatResponse(aiOutput, 'yaml');       // force YAML
 * const msg = parseChatResponse(toolCallArgs, 'json');   // from function call
 * ```
 */
export function parseChatResponse(
  raw: string,
  format: ChatResponseFormat = "auto",
  options?: ChatResponseParserOptions,
): ChatMessage {
  const id = generateId(options?.idGenerator);
  const base: Pick<ChatMessage, "id" | "role" | "timestamp"> = {
    id,
    role: "assistant",
    timestamp: Date.now(),
  };

  const payload = parseRaw(raw, format, options?.yamlParser);
  if (payload) {
    return {
      ...base,
      content: payload.content ?? "",
      severity: payload.severity as StatusLevel | undefined,
      blocks: sanitizeBlocks(payload.blocks),
    };
  }
  return { ...base, content: raw };
}

/**
 * Create a pre-configured response parser.
 *
 * Use this to set global defaults (format, YAML parser, ID generator) so you
 * don't have to pass options on every call.
 *
 * @example
 * ```ts
 * import yaml from 'js-yaml';
 * import { createChatResponseParser } from '@zendir/ui';
 *
 * // Create once at app init
 * export const parseAI = createChatResponseParser({
 *   yamlParser: (s) => yaml.load(s),
 *   defaultFormat: 'yaml',
 * });
 *
 * // Use everywhere
 * const msg = parseAI(aiOutput);
 * ```
 */
export function createChatResponseParser(
  options: ChatResponseParserOptions = {},
) {
  return (raw: string, format?: ChatResponseFormat): ChatMessage => {
    return parseChatResponse(
      raw,
      format ?? options.defaultFormat ?? "auto",
      options,
    );
  };
}

// ─── MCP (Model Context Protocol) Integration ───────────────────────────────

/**
 * Lightweight types for MCP tool results.
 * These mirror the MCP specification without requiring the full SDK as a dependency.
 * @see https://modelcontextprotocol.io/specification/2025-11-25
 */

/** A single content block in an MCP tool result. */
export interface McpToolContent {
  type: "text" | "image" | "resource";
  text?: string;
  data?: string;
  mimeType?: string;
  resource?: { uri: string; text?: string; mimeType?: string };
}

/** MCP tool call result (matches `CallToolResult` from the spec). */
export interface McpToolResult {
  content: McpToolContent[];
  isError?: boolean;
  _meta?: Record<string, unknown>;
}

/**
 * Convert an MCP tool call result into a `ChatMessage` with structured blocks.
 *
 * Handles three patterns automatically:
 * 1. **Structured JSON/YAML** — Tool returns text matching `ChatResponsePayload` → parsed into typed blocks
 * 2. **Plain text** — Rendered as a simple assistant message with tool metadata
 * 3. **Error** — Rendered as a critical alert block
 *
 * This bridges the MCP protocol with ChatPanel's block rendering system, so any
 * MCP server tool can produce rich operator UIs without custom rendering code.
 *
 * @example
 * ```ts
 * import { parseMcpToolResult } from '@zendir/ui/react';
 *
 * // With use-mcp hook
 * const { callTool } = useMcp({ url: 'https://ops-server.example.com/mcp' });
 * const result = await callTool('get_satellite_health', { id: 'SAT-001' });
 * const msg = parseMcpToolResult('get_satellite_health', result);
 * setMessages(prev => [...prev, msg]);
 *
 * // With @modelcontextprotocol/sdk
 * const result = await mcpClient.callTool({ name: 'get_satellite_health', arguments: { id: 'SAT-001' } });
 * const msg = parseMcpToolResult('get_satellite_health', result);
 * ```
 */
export function parseMcpToolResult(
  toolName: string,
  result: McpToolResult,
  options?: { idGenerator?: () => string },
): ChatMessage {
  const id = generateId(options?.idGenerator);
  const timestamp = Date.now();

  // Handle MCP errors
  if (result.isError) {
    const errorText =
      result.content
        ?.filter((c) => c.type === "text")
        .map((c) => c.text)
        .join("\n") || `Tool "${toolName}" failed`;
    return {
      id,
      role: "assistant",
      timestamp,
      severity: "critical" as StatusLevel,
      content: errorText,
      blocks: [
        {
          type: "alert" as const,
          status: "critical" as StatusLevel,
          title: `MCP Tool Error: ${toolName}`,
          detail: errorText,
        },
      ],
    };
  }

  // Extract text content from MCP result
  const textParts =
    result.content?.filter((c) => c.type === "text").map((c) => c.text ?? "") ??
    [];
  const rawText = textParts.join("\n");

  // Try parsing as structured ChatResponsePayload (JSON → YAML → plain)
  if (rawText.trim()) {
    const payload = parseRaw(rawText, "auto");
    if (payload && payload.content) {
      return {
        id,
        role: "assistant",
        timestamp,
        content: payload.content,
        severity: payload.severity as StatusLevel | undefined,
        blocks: sanitizeBlocks(payload.blocks),
      };
    }
  }

  // Plain text fallback with tool context
  return {
    id,
    role: "assistant",
    timestamp,
    content: rawText || `Tool \`${toolName}\` completed successfully.`,
    blocks: [
      {
        type: "kv" as const,
        title: "MCP Tool Call",
        pairs: [
          { key: "Tool", value: toolName, mono: true },
          ...(result._meta
            ? [{ key: "Meta", value: JSON.stringify(result._meta), mono: true }]
            : []),
        ],
      },
    ],
  };
}

/**
 * MCP server tool definition for the `respond_to_operator` tool.
 *
 * Register this on your MCP server to enable structured ChatPanel responses.
 * The schema is identical to `CHAT_RESPONSE_TOOL_SCHEMA` but formatted for the
 * MCP `server.tool()` registration API.
 *
 * Compatible with: `@modelcontextprotocol/sdk`, any MCP server implementation.
 *
 * @example
 * ```ts
 * import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
 * import { CHAT_RESPONSE_MCP_TOOL } from '@zendir/ui';
 *
 * const server = new McpServer({ name: 'ops-assistant', version: '1.0.0' });
 *
 * server.tool(
 *   CHAT_RESPONSE_MCP_TOOL.name,
 *   CHAT_RESPONSE_MCP_TOOL.description,
 *   CHAT_RESPONSE_MCP_TOOL.inputSchema,
 *   async ({ content, severity, blocks }) => ({
 *     content: [{ type: 'text', text: JSON.stringify({ content, severity, blocks }) }],
 *   })
 * );
 * ```
 */
export const CHAT_RESPONSE_MCP_TOOL = {
  name: "respond_to_operator",
  description:
    "Respond to the spacecraft operator with text content and optional structured UI blocks (alerts, telemetry, progress, tables, choices, confirmations, commands, key-value metadata). Use the Astro UX 6-level status system: normal, standby, caution, serious, critical, off.",
  inputSchema: {
    type: "object" as const,
    required: ["content"],
    properties: {
      content: {
        type: "string",
        description: "Plain-text or markdown response shown to the operator.",
      },
      severity: {
        type: "string",
        enum: ["normal", "standby", "caution", "serious", "critical", "off"],
      },
      blocks: {
        type: "array",
        description: "Structured UI blocks rendered below the text content.",
        items: {
          type: "object",
          required: ["type"],
          properties: {
            type: {
              type: "string",
              enum: [
                "alert",
                "telemetry",
                "progress",
                "table",
                "choice",
                "confirm",
                "command",
                "kv",
              ],
            },
            status: {
              type: "string",
              enum: [
                "normal",
                "standby",
                "caution",
                "serious",
                "critical",
                "off",
              ],
            },
            title: { type: "string" },
            detail: { type: "string" },
            label: { type: "string" },
            value: { type: "number" },
            id: { type: "string" },
            command: { type: "string" },
            executable: { type: "boolean" },
            items: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  label: { type: "string" },
                  value: {},
                  unit: { type: "string" },
                  status: { type: "string" },
                },
              },
            },
            columns: { type: "array", items: { type: "string" } },
            rows: { type: "array", items: { type: "array" } },
            options: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: { type: "string" },
                  label: { type: "string" },
                  description: { type: "string" },
                  status: { type: "string" },
                },
              },
            },
            pairs: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  key: { type: "string" },
                  value: {},
                  mono: { type: "boolean" },
                },
              },
            },
          },
        },
      },
    },
  },
} as const;

// ─── Tool / Function calling schema ─────────────────────────────────────────

/**
 * OpenAI-compatible function/tool schema for structured chat responses.
 *
 * Pass this as a tool definition in your API call. The model is constrained
 * to emit valid JSON matching this schema — no prompt engineering required.
 *
 * Works with: OpenAI (Structured Outputs / function calling), Anthropic (Tool Use),
 * Google Gemini (Function Calling), Mistral, Cohere, and any OpenAI-compatible API.
 *
 * @example
 * ```ts
 * // OpenAI
 * tools: [{ type: 'function', function: CHAT_RESPONSE_TOOL_SCHEMA }]
 *
 * // Anthropic
 * tools: [{ name: CHAT_RESPONSE_TOOL_SCHEMA.name,
 *           description: CHAT_RESPONSE_TOOL_SCHEMA.description,
 *           input_schema: CHAT_RESPONSE_TOOL_SCHEMA.parameters }]
 *
 * // Google Gemini
 * tools: [{ functionDeclarations: [CHAT_RESPONSE_TOOL_SCHEMA] }]
 * ```
 */
export const CHAT_RESPONSE_TOOL_SCHEMA = {
  name: "respond_to_operator",
  description:
    "Respond to the spacecraft operator with text content and optional structured UI blocks (alerts, telemetry, progress, tables, choices, confirmations, commands, key-value metadata). Use the Astro UX 6-level status system: normal, standby, caution, serious, critical, off.",
  parameters: {
    type: "object" as const,
    required: ["content"],
    properties: {
      content: {
        type: "string",
        description: "Plain-text or markdown response shown to the operator.",
      },
      severity: {
        type: "string",
        enum: ["normal", "standby", "caution", "serious", "critical", "off"],
        description:
          "Overall message severity. Drives the accent color and icon.",
      },
      blocks: {
        type: "array",
        description: "Structured UI blocks rendered below the text content.",
        items: {
          type: "object",
          required: ["type"],
          properties: {
            type: {
              type: "string",
              enum: [
                "alert",
                "telemetry",
                "progress",
                "table",
                "choice",
                "confirm",
                "command",
                "kv",
              ],
            },
            status: {
              type: "string",
              enum: [
                "normal",
                "standby",
                "caution",
                "serious",
                "critical",
                "off",
              ],
            },
            title: { type: "string" },
            detail: { type: "string" },
            items: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  label: { type: "string" },
                  value: {},
                  unit: { type: "string" },
                  status: {
                    type: "string",
                    enum: [
                      "normal",
                      "standby",
                      "caution",
                      "serious",
                      "critical",
                      "off",
                    ],
                  },
                },
              },
            },
            label: { type: "string" },
            value: { type: "number" },
            max: { type: "number" },
            columns: { type: "array", items: { type: "string" } },
            rows: { type: "array", items: { type: "array" } },
            id: { type: "string" },
            multiple: { type: "boolean" },
            options: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: { type: "string" },
                  label: { type: "string" },
                  description: { type: "string" },
                  status: {
                    type: "string",
                    enum: [
                      "normal",
                      "standby",
                      "caution",
                      "serious",
                      "critical",
                      "off",
                    ],
                  },
                  disabled: { type: "boolean" },
                },
              },
            },
            confirmLabel: { type: "string" },
            cancelLabel: { type: "string" },
            language: { type: "string" },
            command: { type: "string" },
            executable: { type: "boolean" },
            pairs: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  key: { type: "string" },
                  value: {},
                  mono: { type: "boolean" },
                },
              },
            },
          },
        },
      },
    },
  },
} as const;

/**
 * JSON system prompt snippet — instructs the LLM to respond with JSON.
 * Include in your system prompt for the simplest integration path.
 */
export const CHAT_RESPONSE_JSON_PROMPT =
  `When reporting telemetry, status, or alerts, respond with a JSON object:

{
  "content": "Plain text explanation (supports **markdown**).",
  "severity": "normal" | "standby" | "caution" | "serious" | "critical" | "off",
  "blocks": [
    { "type": "alert", "status": "caution", "title": "Alert Title", "detail": "Description" },
    { "type": "telemetry", "title": "Subsystem", "items": [{ "label": "Temp", "value": 72, "unit": "°C", "status": "normal" }] },
    { "type": "progress", "label": "Operation", "value": 75, "max": 100, "status": "normal" },
    { "type": "table", "title": "Data", "columns": ["Col1", "Col2"], "rows": [["val1", "val2"]] },
    { "type": "choice", "id": "unique-id", "title": "Pick one", "options": [{ "id": "opt1", "label": "Option 1" }] },
    { "type": "confirm", "id": "unique-id", "status": "caution", "title": "Confirm action" },
    { "type": "command", "id": "cmd-id", "command": "CMD SAT-001 EXECUTE", "executable": true },
    { "type": "kv", "title": "Context", "pairs": [{ "key": "Spacecraft", "value": "SAT-001" }] }
  ]
}

Status levels (Astro UX): normal (nominal), standby (idle), caution (approaching limit), serious (at limit), critical (exceeding limit), off (no data).
Always include "content". Only include "blocks" when structured data helps the operator.` as const;

/**
 * YAML system prompt snippet — instructs the LLM to respond in compact YAML
 * instead of JSON. ~30-40% fewer output tokens for the same data.
 */
export const CHAT_RESPONSE_YAML_PROMPT =
  `Respond in YAML format (not JSON). Use this structure:

content: Your plain-text or markdown response here.
severity: normal
blocks:
  - type: alert
    status: caution
    title: Alert Title
    detail: Optional detail
  - type: telemetry
    title: Subsystem
    items:
      - label: Parameter
        value: 42.5
        unit: "°C"
        status: normal
  - type: progress
    label: Operation
    value: 75
    max: 100
    status: normal
  - type: choice
    id: unique-id
    title: Pick one
    options:
      - id: opt1
        label: Option 1
        status: normal
  - type: command
    id: cmd-id
    language: CCSDS-TC
    command: CMD SAT-001 EXECUTE
    executable: true
  - type: kv
    title: Context
    pairs:
      - key: Spacecraft
        value: SAT-001

Status levels (Astro UX): normal, standby, caution, serious, critical, off.
Always include "content". Only include "blocks" when structured data helps the operator.` as const;

/**
 * Astro UX status rules snippet — append to any system prompt (JSON or YAML).
 * Gives the LLM concrete thresholds for assigning status levels.
 */
export const CHAT_STATUS_RULES_PROMPT =
  `Assign status levels using these thresholds:
- Battery: normal >50%, standby 30-50%, caution 20-30%, serious 10-20%, critical <10%
- Temperature: normal <70°C, caution 70-80°C, serious 80-85°C, critical >85°C
- Signal: normal >-80 dBm, caution -80 to -90, serious -90 to -95, critical <-95 dBm
- Memory/Storage: normal <70%, caution 70-85%, serious 85-95%, critical >95%

General rules:
- "normal"   → Operating within nominal parameters
- "standby"  → Idle, waiting, or scheduled
- "caution"  → Approaching a limit
- "serious"  → At or exceeding a soft limit
- "critical" → Exceeding hard limit, loss of signal, or failure
- "off"      → Powered down, no data` as const;

// ─── Status shape helper ─────────────────────────────────────────────────────

function StatusShape({
  status,
  size = 10,
  color,
}: {
  status: StatusLevel;
  size?: number;
  color: string;
}) {
  const glow = `${color}50`;
  switch (status) {
    case "caution":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: `drop-shadow(0 0 3px ${glow})` }}
        >
          <rect x="1" y="1" width="10" height="10" fill={color} />
        </svg>
      );
    case "serious":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: `drop-shadow(0 0 3px ${glow})` }}
        >
          <polygon points="6,1 11,6 6,11 1,6" fill={color} />
        </svg>
      );
    case "critical":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: `drop-shadow(0 0 3px ${glow})` }}
        >
          <polygon points="6,11 1,2 11,2" fill={color} />
        </svg>
      );
    case "standby":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: `drop-shadow(0 0 3px ${glow})` }}
        >
          <circle
            cx="6"
            cy="6"
            r="3.5"
            fill="none"
            stroke={color}
            strokeWidth="2"
          />
        </svg>
      );
    case "off":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: `drop-shadow(0 0 3px ${glow})` }}
        >
          <circle cx="6" cy="6" r="3" fill={color} />
        </svg>
      );
    default:
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: `drop-shadow(0 0 3px ${glow})` }}
        >
          <circle cx="6" cy="6" r="5" fill={color} />
        </svg>
      );
  }
}

// ─── Block renderers ─────────────────────────────────────────────────────────

function AlertBlock({ block }: { block: ChatBlockAlert }) {
  const { tokens } = useTheme();
  const color = tokens.colors.status[block.status];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 14px",
        backgroundColor: `${color}12`,
        border: `1px solid ${color}30`,
        borderLeft: `3px solid ${color}`,
        borderRadius: tokens.borderRadius.md,
        marginTop: 8,
      }}
    >
      <div style={{ marginTop: 2, flexShrink: 0 }}>
        <StatusShape status={block.status} size={12} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: 600,
            color: tokens.colors.text.primary,
          }}
        >
          {block.title}
        </div>
        {block.detail && (
          <div
            style={{
              fontSize: tokens.typography.fontSize.xs,
              color: tokens.colors.text.secondary,
              marginTop: 2,
              lineHeight: 1.5,
            }}
          >
            {block.detail}
          </div>
        )}
      </div>
    </div>
  );
}

function TelemetryBlock({ block }: { block: ChatBlockTelemetry }) {
  const { tokens } = useTheme();
  return (
    <div
      style={{
        marginTop: 8,
        border: `1px solid ${tokens.colors.border.muted}`,
        borderRadius: tokens.borderRadius.md,
        overflow: "hidden",
      }}
    >
      {block.title && (
        <div
          style={{
            padding: "6px 12px",
            fontSize: tokens.typography.fontSize.xxs,
            fontWeight: 600,
            color: tokens.colors.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            backgroundColor: `${tokens.colors.background.elevated}80`,
            borderBottom: `1px solid ${tokens.colors.border.muted}`,
          }}
        >
          {block.title}
        </div>
      )}
      <div
        style={{
          padding: "8px 12px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        {(block.items ?? []).map((item, i) => {
          const itemColor = item.status
            ? tokens.colors.status[item.status]
            : tokens.colors.text.secondary;
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                fontSize: tokens.typography.fontSize.sm,
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: tokens.colors.text.secondary,
                }}
              >
                {item.status && (
                  <StatusShape
                    status={item.status}
                    size={8}
                    color={tokens.colors.status[item.status]}
                  />
                )}
                {item.label}
              </span>
              <span
                style={{
                  fontFamily: tokens.typography.fontFamily.mono,
                  fontVariantNumeric: "tabular-nums",
                  color: itemColor,
                  fontWeight: 500,
                }}
              >
                {item.value}
                {item.unit ? ` ${item.unit}` : ""}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProgressBlock({ block }: { block: ChatBlockProgress }) {
  const { tokens } = useTheme();
  const max = block.max ?? 100;
  const pct = Math.min(100, Math.max(0, (block.value / max) * 100));
  const effectiveStatus: StatusLevel =
    block.status ??
    (pct >= 75
      ? "normal"
      : pct >= 50
        ? "standby"
        : pct >= 25
          ? "caution"
          : pct > 0
            ? "serious"
            : "off");
  const color = tokens.colors.status[effectiveStatus];
  return (
    <div style={{ marginTop: 8 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 4,
          fontSize: tokens.typography.fontSize.xs,
          color: tokens.colors.text.secondary,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <StatusShape status={effectiveStatus} size={8} color={color} />
          {block.label}
        </span>
        <span
          style={{
            fontFamily: tokens.typography.fontFamily.mono,
            color: tokens.colors.text.primary,
          }}
        >
          {pct.toFixed(0)}%
        </span>
      </div>
      <div
        style={{
          height: 6,
          backgroundColor: tokens.colors.border.muted,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            backgroundColor: color,
            borderRadius: 3,
            transition: "width 300ms",
            boxShadow: `0 0 6px ${color}40`,
          }}
        />
      </div>
    </div>
  );
}

function TableBlock({ block }: { block: ChatBlockTable }) {
  const { tokens } = useTheme();
  return (
    <div
      style={{
        marginTop: 8,
        border: `1px solid ${tokens.colors.border.muted}`,
        borderRadius: tokens.borderRadius.md,
        overflow: "hidden",
      }}
    >
      {block.title && (
        <div
          style={{
            padding: "6px 12px",
            fontSize: tokens.typography.fontSize.xxs,
            fontWeight: 600,
            color: tokens.colors.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            backgroundColor: `${tokens.colors.background.elevated}80`,
            borderBottom: `1px solid ${tokens.colors.border.muted}`,
          }}
        >
          {block.title}
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: tokens.typography.fontSize.xs,
          }}
        >
          <thead>
            <tr>
              {(block.columns ?? []).map((col, i) => (
                <th
                  key={i}
                  style={{
                    padding: "6px 12px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: tokens.colors.text.secondary,
                    borderBottom: `1px solid ${tokens.colors.border.muted}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(block.rows ?? []).map((row, ri) => (
              <tr key={ri}>
                {(row ?? []).map((cell, ci) => (
                  <td
                    key={ci}
                    style={{
                      padding: "5px 12px",
                      color: tokens.colors.text.primary,
                      fontFamily:
                        typeof cell === "number"
                          ? tokens.typography.fontFamily.mono
                          : undefined,
                      fontVariantNumeric:
                        typeof cell === "number" ? "tabular-nums" : undefined,
                      borderBottom:
                        ri < (block.rows ?? []).length - 1
                          ? `1px solid ${tokens.colors.border.muted}30`
                          : "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActionsBlock({ block }: { block: ChatBlockActions }) {
  const { tokens } = useTheme();
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
      {(block.buttons ?? []).map((btn, i) => {
        const isCritical = btn.variant === "critical";
        const isPrimary =
          btn.variant === "primary" || (!btn.variant && i === 0);
        const baseColor = isCritical
          ? tokens.colors.status.critical
          : tokens.colors.accent.primary;
        const textColor = isCritical
          ? tokens.colors.status.critical
          : safeAccentText(tokens.colors.accent.primary);
        return (
          <button
            key={i}
            onClick={btn.onClick}
            style={{
              fontSize: tokens.typography.fontSize.xxs,
              padding: "4px 14px",
              borderRadius: tokens.borderRadius.sm,
              border: `1px solid ${baseColor}${isPrimary ? "" : "40"}`,
              backgroundColor: isPrimary ? `${baseColor}20` : "transparent",
              color: textColor,
              cursor: "pointer",
              fontFamily: tokens.typography.fontFamily.primary,
              fontWeight: 500,
              transition: "all 150ms",
            }}
          >
            {btn.label}
          </button>
        );
      })}
    </div>
  );
}

function ChoiceBlock({
  block,
  messageId,
  onEvent,
}: {
  block: ChatBlockChoice;
  messageId: string;
  onEvent?: (e: ChatBlockEvent) => void;
}) {
  const { tokens } = useTheme();
  const [localSelected, setLocalSelected] = useState<Set<string>>(
    new Set(block.selected ?? []),
  );
  const isSubmitted = block.submitted;

  const toggle = (optId: string) => {
    if (isSubmitted) return;
    setLocalSelected((prev) => {
      const next = new Set(prev);
      if (block.multiple) {
        next.has(optId) ? next.delete(optId) : next.add(optId);
      } else {
        next.clear();
        next.add(optId);
      }
      return next;
    });
  };

  const submit = () => {
    if (localSelected.size === 0 || isSubmitted) return;
    onEvent?.({
      blockId: block.id,
      messageId,
      action: "select",
      value: Array.from(localSelected),
    });
  };

  return (
    <div style={{ marginTop: 8 }}>
      {block.title && (
        <div
          style={{
            fontSize: tokens.typography.fontSize.xs,
            fontWeight: 600,
            color: tokens.colors.text.secondary,
            marginBottom: 6,
          }}
        >
          {block.title}
          {block.multiple ? " (select multiple)" : ""}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {(block.options ?? []).map((opt) => {
          const isSelected = localSelected.has(opt.id);
          const optColor = opt.status
            ? tokens.colors.status[opt.status]
            : tokens.colors.accent.primary;
          const isDisabled = opt.disabled || isSubmitted;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => toggle(opt.id)}
              disabled={isDisabled}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 12px",
                borderRadius: tokens.borderRadius.md,
                cursor: isDisabled ? "default" : "pointer",
                border: `1px solid ${isSelected ? optColor : tokens.colors.border.muted}`,
                backgroundColor: isSelected ? `${optColor}15` : "transparent",
                transition: "all 150ms",
                textAlign: "left",
                fontFamily: tokens.typography.fontFamily.primary,
                opacity: isDisabled && !isSelected ? 0.5 : 1,
              }}
            >
              {/* Radio / checkbox indicator */}
              <div
                style={{
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                  borderRadius: block.multiple ? 3 : "50%",
                  border: `2px solid ${isSelected ? optColor : tokens.colors.border.muted}`,
                  backgroundColor: isSelected ? optColor : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 150ms",
                }}
              >
                {isSelected && (
                  <svg
                    viewBox="0 0 12 12"
                    width={10}
                    height={10}
                    fill="none"
                    stroke={tokens.colors.text.inverse}
                    strokeWidth={2}
                  >
                    <polyline points="2,6 5,9 10,3" />
                  </svg>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {opt.status && (
                    <StatusShape
                      status={opt.status}
                      size={8}
                      color={tokens.colors.status[opt.status]}
                    />
                  )}
                  <span
                    style={{
                      fontSize: tokens.typography.fontSize.sm,
                      color: tokens.colors.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    {opt.label}
                  </span>
                </div>
                {opt.description && (
                  <div
                    style={{
                      fontSize: tokens.typography.fontSize.xs,
                      color: tokens.colors.text.tertiary,
                      marginTop: 1,
                    }}
                  >
                    {opt.description}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
      {!isSubmitted && onEvent && (
        <button
          type="button"
          onClick={submit}
          disabled={localSelected.size === 0}
          style={{
            marginTop: 8,
            padding: "5px 18px",
            fontSize: tokens.typography.fontSize.xs,
            fontWeight: 600,
            borderRadius: tokens.borderRadius.sm,
            border: `1px solid ${tokens.colors.accent.primary}`,
            backgroundColor:
              localSelected.size > 0
                ? `${tokens.colors.accent.primary}20`
                : "transparent",
            color:
              localSelected.size > 0
                ? safeAccentText(tokens.colors.accent.primary)
                : tokens.colors.text.tertiary,
            cursor: localSelected.size > 0 ? "pointer" : "not-allowed",
            fontFamily: tokens.typography.fontFamily.primary,
            transition: "all 150ms",
          }}
        >
          Confirm Selection
        </button>
      )}
      {isSubmitted && (
        <div
          style={{
            marginTop: 6,
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.text.tertiary,
            fontStyle: "italic",
          }}
        >
          Selection submitted
        </div>
      )}
    </div>
  );
}

function ConfirmBlock({
  block,
  messageId,
  onEvent,
}: {
  block: ChatBlockConfirm;
  messageId: string;
  onEvent?: (e: ChatBlockEvent) => void;
}) {
  const { tokens } = useTheme();
  const status = block.status ?? "caution";
  const color = tokens.colors.status[status];
  const resolved = block.resolved;

  return (
    <div
      style={{
        marginTop: 8,
        padding: "12px 14px",
        backgroundColor: `${color}10`,
        border: `1px solid ${color}30`,
        borderLeft: `3px solid ${color}`,
        borderRadius: tokens.borderRadius.md,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <StatusShape status={status} size={12} color={color} />
        <span
          style={{
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: 600,
            color: tokens.colors.text.primary,
          }}
        >
          {block.title}
        </span>
      </div>
      {block.detail && (
        <div
          style={{
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.text.secondary,
            marginBottom: 8,
            lineHeight: 1.5,
          }}
        >
          {block.detail}
        </div>
      )}
      {!resolved && onEvent ? (
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() =>
              onEvent({ blockId: block.id, messageId, action: "confirm" })
            }
            style={{
              padding: "5px 16px",
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: 600,
              borderRadius: tokens.borderRadius.sm,
              cursor: "pointer",
              border: `1px solid ${color}`,
              backgroundColor: `${color}20`,
              color,
              fontFamily: tokens.typography.fontFamily.primary,
              transition: "all 150ms",
            }}
          >
            {block.confirmLabel ?? "Confirm"}
          </button>
          <button
            type="button"
            onClick={() =>
              onEvent({ blockId: block.id, messageId, action: "cancel" })
            }
            style={{
              padding: "5px 16px",
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: 500,
              borderRadius: tokens.borderRadius.sm,
              cursor: "pointer",
              border: `1px solid ${tokens.colors.border.muted}`,
              backgroundColor: "transparent",
              color: tokens.colors.text.secondary,
              fontFamily: tokens.typography.fontFamily.primary,
              transition: "all 150ms",
            }}
          >
            {block.cancelLabel ?? "Cancel"}
          </button>
        </div>
      ) : resolved ? (
        <div
          style={{
            fontSize: tokens.typography.fontSize.xs,
            fontWeight: 500,
            color:
              resolved === "confirmed"
                ? tokens.colors.status.normal
                : tokens.colors.text.tertiary,
            fontStyle: "italic",
          }}
        >
          {resolved === "confirmed" ? "✓ Confirmed" : "✗ Cancelled"}
        </div>
      ) : null}
    </div>
  );
}

function CommandBlock({
  block,
  messageId,
  onEvent,
}: {
  block: ChatBlockCommand;
  messageId: string;
  onEvent?: (e: ChatBlockEvent) => void;
}) {
  const { tokens } = useTheme();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(block.command).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
    if (block.id)
      onEvent?.({
        blockId: block.id,
        messageId,
        action: "copy",
        value: block.command,
      });
  };

  const handleExecute = () => {
    if (block.id)
      onEvent?.({
        blockId: block.id,
        messageId,
        action: "execute",
        value: block.command,
      });
  };

  return (
    <div
      style={{
        marginTop: 8,
        borderRadius: tokens.borderRadius.md,
        overflow: "hidden",
        border: `1px solid ${tokens.colors.border.muted}`,
      }}
    >
      {block.label && (
        <div
          style={{
            padding: "5px 12px",
            fontSize: tokens.typography.fontSize.xxs,
            fontWeight: 600,
            color: tokens.colors.text.tertiary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            backgroundColor: `${tokens.colors.background.elevated}80`,
            borderBottom: `1px solid ${tokens.colors.border.muted}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>
            {block.label}
            {block.language ? ` · ${block.language}` : ""}
          </span>
        </div>
      )}
      <div style={{ position: "relative" }}>
        <pre
          style={{
            margin: 0,
            padding: "10px 12px",
            paddingRight: 80,
            fontSize: tokens.typography.fontSize.xs,
            fontFamily: tokens.typography.fontFamily.mono,
            backgroundColor: `${tokens.colors.background.base}80`,
            color: tokens.colors.text.primary,
            overflowX: "auto",
            lineHeight: 1.5,
          }}
        >
          {block.command}
        </pre>
        <div
          style={{
            position: "absolute",
            top: 6,
            right: 8,
            display: "flex",
            gap: 4,
          }}
        >
          <button
            type="button"
            onClick={handleCopy}
            style={{
              padding: "2px 8px",
              fontSize: tokens.typography.fontSize.micro,
              borderRadius: tokens.borderRadius.sm,
              border: `1px solid ${tokens.colors.border.muted}`,
              backgroundColor: tokens.colors.background.elevated,
              color: copied
                ? tokens.colors.status.normal
                : tokens.colors.text.tertiary,
              cursor: "pointer",
              fontFamily: tokens.typography.fontFamily.primary,
              fontWeight: 500,
            }}
          >
            {copied ? "✓ Copied" : "Copy"}
          </button>
          {block.executable && onEvent && block.id && (
            <button
              type="button"
              onClick={handleExecute}
              style={{
                padding: "2px 8px",
                fontSize: tokens.typography.fontSize.micro,
                borderRadius: tokens.borderRadius.sm,
                border: `1px solid ${tokens.colors.accent.primary}60`,
                backgroundColor: `${tokens.colors.accent.primary}15`,
                color: safeAccentText(tokens.colors.accent.primary),
                cursor: "pointer",
                fontFamily: tokens.typography.fontFamily.primary,
                fontWeight: 600,
              }}
            >
              Run
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function KVBlock({ block }: { block: ChatBlockKV }) {
  const { tokens } = useTheme();
  return (
    <div
      style={{
        marginTop: 8,
        padding: "8px 12px",
        backgroundColor: `${tokens.colors.background.elevated}40`,
        borderRadius: tokens.borderRadius.md,
        border: `1px solid ${tokens.colors.border.muted}`,
      }}
    >
      {block.title && (
        <div
          style={{
            fontSize: tokens.typography.fontSize.xxs,
            fontWeight: 600,
            color: tokens.colors.text.tertiary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 6,
          }}
        >
          {block.title}
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: "3px 16px",
          fontSize: tokens.typography.fontSize.xs,
        }}
      >
        {(block.pairs ?? []).map((p, i) => (
          <React.Fragment key={i}>
            <span
              style={{
                color: tokens.colors.text.tertiary,
                whiteSpace: "nowrap",
              }}
            >
              {p.key}
            </span>
            <span
              style={{
                color: tokens.colors.text.primary,
                fontFamily: p.mono
                  ? tokens.typography.fontFamily.mono
                  : undefined,
                fontVariantNumeric: p.mono ? "tabular-nums" : undefined,
              }}
            >
              {p.value}
            </span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function renderBlock(
  block: ChatBlock,
  index: number,
  messageId: string,
  onEvent?: (e: ChatBlockEvent) => void,
) {
  switch (block.type) {
    case "alert":
      return <AlertBlock key={index} block={block} />;
    case "telemetry":
      return <TelemetryBlock key={index} block={block} />;
    case "progress":
      return <ProgressBlock key={index} block={block} />;
    case "table":
      return <TableBlock key={index} block={block} />;
    case "actions":
      return <ActionsBlock key={index} block={block} />;
    case "choice":
      return (
        <ChoiceBlock
          key={index}
          block={block}
          messageId={messageId}
          onEvent={onEvent}
        />
      );
    case "confirm":
      return (
        <ConfirmBlock
          key={index}
          block={block}
          messageId={messageId}
          onEvent={onEvent}
        />
      );
    case "command":
      return (
        <CommandBlock
          key={index}
          block={block}
          messageId={messageId}
          onEvent={onEvent}
        />
      );
    case "kv":
      return <KVBlock key={index} block={block} />;
    default:
      return null;
  }
}

// ─── Main component ──────────────────────────────────────────────────────────

export const ChatPanel = memo(function ChatPanel({
  messages = [],
  onSend,
  loading = false,
  placeholder = "Type a message...",
  title,
  subtitle,
  disabled = false,
  showTimestamps = true,
  height = "100%",
  onClear,
  onBlockEvent,
  renderMessageContent,
  style,
}: ChatPanelProps): React.ReactElement {
  const { tokens } = useTheme();
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || disabled) return;
    onSend(text);
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
  }, [input, onSend, disabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInput(e.target.value);
      const el = e.target;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    },
    [],
  );

  const formatTime = (ts?: number) => {
    if (!ts) return "";
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Markdown-ish rendering: **bold**, `code`, ```code blocks```. Lists, tables
  // and links are NOT handled — pass `renderMessageContent` for those.
  const renderContent = (text: string) => {
    const parts: React.ReactNode[] = [];
    const lines = text.split("\n");
    let inCode = false;
    let codeBlock: string[] = [];

    lines.forEach((line, i) => {
      if (line.startsWith("```")) {
        if (inCode) {
          parts.push(
            <pre
              key={`code-${i}`}
              style={{
                backgroundColor: `${tokens.colors.background.base}80`,
                padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
                borderRadius: tokens.borderRadius.sm,
                fontSize: tokens.typography.fontSize.xs,
                fontFamily: tokens.typography.fontFamily.mono,
                overflowX: "auto",
                margin: `${tokens.spacing.xs} 0`,
                border: `1px solid ${tokens.colors.border.muted}`,
              }}
            >
              {codeBlock.join("\n")}
            </pre>,
          );
          codeBlock = [];
          inCode = false;
        } else {
          inCode = true;
        }
        return;
      }
      if (inCode) {
        codeBlock.push(line);
        return;
      }

      let processed: React.ReactNode = line;
      if (line.includes("**")) {
        const segments = line.split(/\*\*(.*?)\*\*/g);
        processed = segments.map((seg, j) =>
          j % 2 === 1 ? <strong key={j}>{seg}</strong> : seg,
        );
      }
      if (typeof processed === "string" && processed.includes("`")) {
        const segments = processed.split(/`(.*?)`/g);
        processed = segments.map((seg, j) =>
          j % 2 === 1 ? (
            <code
              key={j}
              style={{
                backgroundColor: `${tokens.colors.background.base}80`,
                padding: "1px 4px",
                borderRadius: "3px",
                fontSize: "0.9em",
                fontFamily: tokens.typography.fontFamily.mono,
              }}
            >
              {seg}
            </code>
          ) : (
            seg
          ),
        );
      }
      parts.push(
        <React.Fragment key={`line-${i}`}>
          {processed}
          {i < lines.length - 1 && <br />}
        </React.Fragment>,
      );
    });
    return parts;
  };

  const TypingDots = () => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        padding: tokens.spacing.xs,
      }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: tokens.colors.accent.primary,
            opacity: 0.6,
            animation: `zendir-typing 1.2s ease-in-out ${i * 0.2}s infinite`,
          }}
        />
      ))}
      <style>{`@keyframes zendir-typing { 0%,60%,100%{opacity:.3;transform:translateY(0)} 30%{opacity:1;transform:translateY(-3px)} }`}</style>
    </div>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: typeof height === "number" ? `${height}px` : height,
        fontFamily: tokens.typography.fontFamily.primary,
        backgroundColor: tokens.colors.background.base,
        borderRadius: tokens.borderRadius.lg,
        border: `1px solid ${tokens.colors.border.muted}`,
        overflow: "hidden",
        ...style,
      }}
    >
      {/* Header */}
      {(title || onClear) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
            borderBottom: `1px solid ${tokens.colors.border.muted}`,
            backgroundColor: tokens.colors.background.surface,
          }}
        >
          <div>
            {title && (
              <div
                style={{
                  fontSize: tokens.typography.fontSize.sm,
                  fontWeight: 600,
                  color: tokens.colors.text.primary,
                }}
              >
                {title}
              </div>
            )}
            {subtitle && (
              <div
                style={{
                  fontSize: tokens.typography.fontSize.xxs,
                  color: tokens.colors.text.tertiary,
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          {onClear && messages.length > 0 && (
            <button
              onClick={onClear}
              style={{
                fontSize: tokens.typography.fontSize.xxs,
                color: tokens.colors.text.tertiary,
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: tokens.borderRadius.sm,
                fontFamily: tokens.typography.fontFamily.primary,
              }}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Messages — aria-live so screen readers announce new messages */}
      <div
        role="log"
        aria-live="polite"
        aria-label="Chat messages"
        style={{
          flex: 1,
          overflow: "auto",
          padding: tokens.spacing.md,
          display: "flex",
          flexDirection: "column",
          gap: tokens.spacing.sm,
        }}
      >
        {messages.length === 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              color: tokens.colors.text.tertiary,
              fontSize: tokens.typography.fontSize.sm,
            }}
          >
            No messages yet
          </div>
        )}

        {messages.map((msg) => {
          const isUser = msg.role === "user";
          const isSystem = msg.role === "system";
          const severity = msg.severity;
          const severityColor = severity
            ? tokens.colors.status[severity]
            : undefined;

          // System message accent: uses severity if set, otherwise default caution tint
          const systemBg = severityColor
            ? `${severityColor}12`
            : `${tokens.colors.status.caution}10`;
          const systemBorder = severityColor
            ? `${severityColor}30`
            : `${tokens.colors.status.caution}20`;
          const systemAccent = severityColor || undefined;

          return (
            <div
              key={msg.id}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: isUser ? "flex-end" : "flex-start",
                maxWidth: isSystem ? "100%" : "85%",
                alignSelf: isUser ? "flex-end" : "flex-start",
              }}
            >
              <div
                style={{
                  padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
                  borderRadius: tokens.borderRadius.md,
                  backgroundColor: isUser
                    ? `${tokens.colors.accent.primary}20`
                    : isSystem
                      ? systemBg
                      : tokens.colors.background.surface,
                  border: `1px solid ${
                    isUser
                      ? `${tokens.colors.accent.primary}30`
                      : isSystem
                        ? systemBorder
                        : tokens.colors.border.muted
                  }`,
                  borderLeft:
                    isSystem && systemAccent
                      ? `3px solid ${systemAccent}`
                      : undefined,
                  fontSize: tokens.typography.fontSize.sm,
                  color: tokens.colors.text.primary,
                  lineHeight: 1.6,
                  width: isSystem ? "100%" : "auto",
                  opacity: msg.status === "sending" ? 0.7 : 1,
                }}
              >
                {/* Severity indicator for system messages */}
                {isSystem && severity && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 4,
                    }}
                  >
                    <StatusShape
                      status={severity}
                      size={10}
                      color={severityColor!}
                    />
                    <span
                      style={{
                        fontSize: tokens.typography.fontSize.xxs,
                        fontWeight: 600,
                        color: severityColor,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {severity}
                    </span>
                  </div>
                )}

                {renderMessageContent
                  ? renderMessageContent(msg.content, msg)
                  : renderContent(msg.content)}

                {/* Structured blocks */}
                {Array.isArray(msg.blocks) &&
                  msg.blocks.map((block, i) =>
                    block ? renderBlock(block, i, msg.id, onBlockEvent) : null,
                  )}

                {/* Legacy action buttons */}
                {msg.actions && msg.actions.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: tokens.spacing.xs,
                      marginTop: tokens.spacing.xs,
                      paddingTop: tokens.spacing.xs,
                      borderTop: `1px solid ${tokens.colors.border.muted}`,
                    }}
                  >
                    {msg.actions.map((action, i) => (
                      <button
                        key={i}
                        onClick={action.onClick}
                        style={{
                          fontSize: tokens.typography.fontSize.xxs,
                          padding: "2px 10px",
                          borderRadius: tokens.borderRadius.sm,
                          border: `1px solid ${tokens.colors.accent.primary}40`,
                          backgroundColor: `${tokens.colors.accent.primary}10`,
                          color: safeAccentText(tokens.colors.accent.primary),
                          cursor: "pointer",
                          fontFamily: tokens.typography.fontFamily.primary,
                        }}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {showTimestamps && msg.timestamp && (
                <div
                  style={{
                    fontSize: tokens.typography.fontSize.micro,
                    color: tokens.colors.text.tertiary,
                    marginTop: "2px",
                    paddingLeft: tokens.spacing.xs,
                    paddingRight: tokens.spacing.xs,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {formatTime(msg.timestamp)}
                  {msg.status === "error" && (
                    <span style={{ color: tokens.colors.status.critical }}>
                      • Failed
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {loading && (
          <div
            style={{
              alignSelf: "flex-start",
              padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
              borderRadius: tokens.borderRadius.md,
              backgroundColor: tokens.colors.background.surface,
              border: `1px solid ${tokens.colors.border.muted}`,
            }}
          >
            <TypingDots />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: tokens.spacing.sm,
          padding: tokens.spacing.md,
          borderTop: `1px solid ${tokens.colors.border.muted}`,
          backgroundColor: tokens.colors.background.surface,
        }}
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          aria-label="Message input"
          rows={1}
          style={{
            flex: 1,
            resize: "none",
            border: `1px solid ${tokens.colors.border.muted}`,
            borderRadius: tokens.borderRadius.md,
            padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
            fontSize: tokens.typography.fontSize.sm,
            fontFamily: tokens.typography.fontFamily.primary,
            color: tokens.colors.text.primary,
            backgroundColor: tokens.colors.background.base,
            outline: "none",
            transition: "border-color 150ms, background-color 150ms",
            maxHeight: 120,
            lineHeight: 1.5,
            overflowY: "auto",
          }}
        />
        <button
          onClick={handleSend}
          disabled={disabled || !input.trim()}
          aria-label="Send message"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 44,
            height: 44,
            borderRadius: tokens.borderRadius.md,
            border: "none",
            backgroundColor: input.trim()
              ? tokens.colors.accent.primary
              : `${tokens.colors.accent.primary}20`,
            color: input.trim()
              ? tokens.colors.text.inverse
              : tokens.colors.text.tertiary,
            cursor: input.trim() && !disabled ? "pointer" : "not-allowed",
            transition: tokens.animation.fast,
            flexShrink: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
});

export default ChatPanel;
